import { useEffect, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  getTeamLevel,
  getTeamMember,
  getTeamMemberInstallments,
  searchTeam,
} from '../../../infrastructure/api/hierarchyApi';
import { qKeys } from '../keys';
import { queryDefaults } from '../queryPolicy';

export interface TeamFilters {
  tier: 'ALL' | 'L1' | 'L2' | 'L3+';
  status: 'ALL' | 'OVERDUE' | 'INACTIVE' | 'CLOSED';
  sort: 'overdue' | 'name' | 'joined' | 'progress';
}

export const DEFAULT_TEAM_FILTERS: TeamFilters = { tier: 'ALL', status: 'ALL', sort: 'overdue' };

const PAGE_SIZE = 50;

/** E22 — one navigator level, paged 50 at a time ("Load more"). `focus` null = the viewer. */
export function useTeamLevel(focus: string | null, filters: TeamFilters = DEFAULT_TEAM_FILTERS) {
  const id = focus ?? 'me';
  return useInfiniteQuery({
    queryKey: qKeys.team.level(id, filters),
    queryFn: ({ pageParam }) => getTeamLevel(id, { ...filters, page: pageParam, limit: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.limit < last.total ? last.page + 1 : undefined),
    placeholderData: keepPreviousData,
    ...queryDefaults,
    staleTime: 60_000,
  });
}

/** E23 — Member 360 header + Profile tab. */
export function useTeamMember(userId: string | null) {
  return useQuery({
    queryKey: qKeys.team.member(userId ?? ''),
    queryFn: () => getTeamMember(userId as string),
    enabled: Boolean(userId),
    ...queryDefaults,
    staleTime: 60_000,
  });
}

/** E24 — fetched lazily, only once the Installments tab is open. */
export function useTeamMemberInstallments(userId: string, subscriptionId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qKeys.team.installments(userId, subscriptionId),
    queryFn: () => getTeamMemberInstallments(userId, subscriptionId),
    enabled,
    ...queryDefaults,
    staleTime: 60_000,
  });
}

/** E25 — debounced (300 ms) subtree search; idle below 2 characters. */
export function useTeamSearch(raw: string) {
  const [q, setQ] = useState(raw.trim());
  useEffect(() => {
    const handle = setTimeout(() => setQ(raw.trim()), 300);
    return () => clearTimeout(handle);
  }, [raw]);
  const query = useQuery({
    queryKey: qKeys.team.search(q),
    queryFn: () => searchTeam(q),
    enabled: q.length >= 2,
    ...queryDefaults,
    staleTime: 30_000,
  });
  return { ...query, debouncedQuery: q };
}
